import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";

import { GraphicOvercommision, GraphicNumDoc } from '@app/shared/models/ipm/GraphicData';
import { BarChartData } from '@app/shared/models/ipm/BarChartData';

import { colors } from '@app/charts/colors'
import { default as constantsFile } from '@app/shared/constants.json';


@Component({
    selector: 'app-charts-list',
    templateUrl: './charts-list.component.html',
    styleUrls: ['./charts-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class ChartsListComponent implements OnInit, OnDestroy {

  alive: boolean = true;
  barPadding: number;
  colors: any;
  constants: any = constantsFile.constants;
  limitDays: string = "Display days";
  limitMonths: string = "Display months";
  modeSelected: string = this.constants.charts.days;
  noWeeks: boolean = true;
  noMonths: boolean = true;
  timeOptions: Array<any>;

  days: string = this.constants.charts.days;
  weeks: string = this.constants.charts.weeks;
  months: string = this.constants.charts.months;

  _aOvercommisionData: Array<GraphicOvercommision>;
  _aNumDocData: Array<GraphicNumDoc>;
  _aColorsData: any;
  _bColorsData: any;

  @Input() currencyCode: string;
  @Input() fromDate : Date;
  @Input() toDate: Date;

  @Input() barCharts: BarChartData;
  @Input() maxDaysInGraphics: number;
  @Input() maxMonthsInGraphics: number;
  @Input() userLocale: string;

  constructor(
    private translateService: TranslateService
  ) {
    this.barPadding = 25;
    Object.assign(this, { colors });
  }

  ngOnInit(): void {
    if (this.barCharts && this.barCharts.graphicsByDays) {
      this._aOvercommisionData = this.barCharts.graphicsByDays.map(d => new GraphicOvercommision(d.name, Math.round(d.valueOvercommision)));
      this._aNumDocData = this.barCharts.graphicsByDays.map(d => new GraphicNumDoc(d.name, d.valueNumDoc));
      this._aColorsData = this.colors.find(c => c.name === "blueAccelya");
      this._bColorsData = this.colors.find(c => c.name === "violetAccelya");
    }
    this.configureLimits();
    this.timeOptions = [
      { label: this.translateService.instant('Days'), value: this.days },
      { label: this.translateService.instant('Weeks'), value: this.weeks, isDisabled: this.noWeeks },
      { label: this.translateService.instant('Months'), value: this.months, isDisabled: this.noMonths }
    ];
  }

  /**
   * Configurar los límites, tanto para saber si "Semana" o "Mes" se puede seleccionar como para saber si hay que limitar la visualización
   */
  private configureLimits() {
    // Calcular el tiempo entre el rango de fechas seleccionado
    const diffDates = this.toDate.valueOf() - this.fromDate.valueOf();

    // Variables para el cálculo de los límites
    // Horas * minutos * segundos * milisegundos
    const convertDaysToMilliseconds = 24 * 60 * 60 * 1000;
    const maxDaysms = this.maxDaysInGraphics * convertDaysToMilliseconds;
    // Días * Horas * minutos * segundos * milisegundos
    const maxMonthsms = this.maxMonthsInGraphics * 30 * 24 * 60 * 60 * 1000;

    const daysForWeeks = 10;
    const daysForWeeksMilliseconds = daysForWeeks * convertDaysToMilliseconds;
    // Cuando haya que limitar, mostrar el tool tip con mensaje
    this.limitDays = (diffDates > maxDaysms) ? "Display days" : "";
    this.limitMonths = (diffDates > maxMonthsms) ? "Display months" : "";
    // Controlar cuándo no hay suficientes días para alguno de estos dos modos
    this.noWeeks = (diffDates < daysForWeeksMilliseconds);
    this.noMonths = (this.toDate.getMonth() === this.fromDate.getMonth());
  }

  public getGraphics() {
    if(this.modeSelected){ // This is because selectbutton component can get a double click on the same button
      this._aOvercommisionData = this.barCharts['graphicsBy'+ this.modeSelected].map(d => new GraphicOvercommision(d.name, Math.round(d.valueOvercommision)));
      this._aNumDocData = this.barCharts['graphicsBy'+ this.modeSelected].map(d => new GraphicNumDoc(d.name, d.valueNumDoc))
      this._aColorsData = this.colors.find(c => c.name === "blueAccelya");
      this._bColorsData = this.colors.find(c => c.name === "violetAccelya");
    }
  }

  public formatNumber(numeric: number): string{
    return Math.round(numeric).toLocaleString(this.userLocale);
  }

  public formatY(x) {
    return x.toLocaleString(this.userLocale);
  }

  ngOnDestroy() {
		// prevent memory leak when component destroyed
		this.alive = false;
  }
}
